import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Static fallbacks from qualificationOptions.ts
import {
  US_ENTRY_METHODS,
  GENDER_OPTIONS,
  LANGUAGE_OPTIONS,
  COUNTRY_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  SPOUSE_IMMIGRATION_STATUS,
  CURRENT_IMMIGRATION_STATUS,
  QUALIFICATION_OBJECTIVES,
  URGENCY_OPTIONS,
  REASON_SEEKING_NEW_SERVICE,
  EAD_STATUS_OPTIONS,
  US_ENTRY_COUNT_OPTIONS,
  ICE_CHECKIN_TIMELINE,
  IMMIGRATION_PROCESS_TYPES,
  PROCESS_STATUS_OPTIONS,
} from '@/lib/qualificationOptions';

// Mapping of list_key to static fallback
const STATIC_FALLBACKS: Record<string, readonly { value: string; label: string }[]> = {
  'us_entry_methods': US_ENTRY_METHODS,
  'gender_options': GENDER_OPTIONS,
  'language_options': LANGUAGE_OPTIONS,
  'country_options': COUNTRY_OPTIONS,
  'marital_status_options': MARITAL_STATUS_OPTIONS,
  'spouse_immigration_status': SPOUSE_IMMIGRATION_STATUS,
  'current_immigration_status': CURRENT_IMMIGRATION_STATUS,
  'qualification_objectives': QUALIFICATION_OBJECTIVES,
  'urgency_options': URGENCY_OPTIONS,
  'reason_seeking_new_service': REASON_SEEKING_NEW_SERVICE,
  'ead_status_options': EAD_STATUS_OPTIONS,
  'us_entry_count_options': US_ENTRY_COUNT_OPTIONS,
  'ice_checkin_timeline': ICE_CHECKIN_TIMELINE,
  'immigration_process_types': IMMIGRATION_PROCESS_TYPES,
  'process_status_options': PROCESS_STATUS_OPTIONS,
};

/**
 * Hook to fetch qualification options by list_key with fallback to static data
 */
export function useQualificationOptionsByKey(listKey: string) {
  return useQuery({
    queryKey: ['qualification-options-by-key', listKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('qualification_option_lists')
        .select(`
          id, 
          list_key, 
          qualification_options(id, value, label, sort_order, is_active)
        `)
        .eq('list_key', listKey)
        .single();
      
      if (error) {
        console.warn(`Failed to fetch options for ${listKey}, using fallback:`, error.message);
        return STATIC_FALLBACKS[listKey] || [];
      }
      
      const options = (data?.qualification_options || [])
        .filter((o: any) => o.is_active)
        .sort((a: any, b: any) => a.sort_order - b.sort_order)
        .map((o: any) => ({ value: o.value, label: o.label }));
      
      // If no options from DB, use fallback
      if (options.length === 0) {
        return STATIC_FALLBACKS[listKey] || [];
      }
      
      return options;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

export interface QualificationOption {
  id: string;
  list_id: string;
  value: string;
  label: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface QualificationOptionList {
  id: string;
  list_key: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  options?: QualificationOption[];
}

export function useQualificationOptionLists() {
  return useQuery({
    queryKey: ['qualification-option-lists'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('qualification_option_lists')
        .select('*')
        .order('name');

      if (error) throw error;
      return data as QualificationOptionList[];
    }
  });
}

export function useQualificationOptions(listId: string | null) {
  return useQuery({
    queryKey: ['qualification-options', listId],
    queryFn: async () => {
      if (!listId) return [];
      
      const { data, error } = await supabase
        .from('qualification_options')
        .select('*')
        .eq('list_id', listId)
        .order('sort_order');

      if (error) throw error;
      return data as QualificationOption[];
    },
    enabled: !!listId
  });
}

export function useQualificationOptionsWithLists() {
  return useQuery({
    queryKey: ['qualification-options-with-lists'],
    queryFn: async () => {
      const { data: lists, error: listsError } = await supabase
        .from('qualification_option_lists')
        .select('*')
        .order('name');

      if (listsError) throw listsError;

      const { data: options, error: optionsError } = await supabase
        .from('qualification_options')
        .select('*')
        .order('sort_order');

      if (optionsError) throw optionsError;

      // Group options by list_id
      const listsWithOptions = (lists as QualificationOptionList[]).map(list => ({
        ...list,
        options: (options as QualificationOption[]).filter(opt => opt.list_id === list.id)
      }));

      return listsWithOptions;
    }
  });
}

export function useCreateQualificationOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      list_id: string;
      value: string;
      label: string;
      sort_order?: number;
    }) => {
      // Get max sort_order for the list if not provided
      let sortOrder = data.sort_order;
      if (sortOrder === undefined) {
        const { data: existing } = await supabase
          .from('qualification_options')
          .select('sort_order')
          .eq('list_id', data.list_id)
          .order('sort_order', { ascending: false })
          .limit(1);

        sortOrder = existing && existing.length > 0 ? existing[0].sort_order + 1 : 0;
      }

      const { data: newOption, error } = await supabase
        .from('qualification_options')
        .insert({
          list_id: data.list_id,
          value: data.value,
          label: data.label,
          sort_order: sortOrder
        })
        .select()
        .single();

      if (error) throw error;
      return newOption;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualification-options'] });
      queryClient.invalidateQueries({ queryKey: ['qualification-options-with-lists'] });
      toast.success('Opção adicionada com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao adicionar opção: ' + error.message);
    }
  });
}

export function useUpdateQualificationOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: {
      id: string;
      value?: string;
      label?: string;
      sort_order?: number;
      is_active?: boolean;
    }) => {
      const { data: updated, error } = await supabase
        .from('qualification_options')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualification-options'] });
      queryClient.invalidateQueries({ queryKey: ['qualification-options-with-lists'] });
      toast.success('Opção atualizada com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao atualizar opção: ' + error.message);
    }
  });
}

export function useDeleteQualificationOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('qualification_options')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualification-options'] });
      queryClient.invalidateQueries({ queryKey: ['qualification-options-with-lists'] });
      toast.success('Opção removida com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao remover opção: ' + error.message);
    }
  });
}

export function useReorderQualificationOptions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: { id: string; sort_order: number }[]) => {
      // Update each option's sort_order
      const promises = updates.map(({ id, sort_order }) =>
        supabase
          .from('qualification_options')
          .update({ sort_order })
          .eq('id', id)
      );

      const results = await Promise.all(promises);
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        throw new Error('Erro ao reordenar opções');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualification-options'] });
      queryClient.invalidateQueries({ queryKey: ['qualification-options-with-lists'] });
    },
    onError: (error) => {
      toast.error('Erro ao reordenar: ' + error.message);
    }
  });
}
